/**
 * Schedule-specific Vuex store.
 *
 * Extends the shared BlueSearch / search store with URL state synchronization
 * for schedule-specific parameters: `sort` and `day`.
 *
 * Supported query parameters:
 *   q       – search term         (reuses BlueSearch plumbing via queryStringKeyQuery)
 *   filters – comma-separated IDs (reuses BlueSearch plumbing, excludes session_day)
 *   sort    – composite fieldName_sortMode (schedule extension, e.g. "title_normalized_asc")
 *   day     – ISO date string     (schedule extension, e.g. "2025-09-05")
 *   page    – 1-based page number (reuses BlueSearch plumbing)
 *
 * Day filter uses a two-phase initialization:
 *   Phase 1 – unfiltered search captures all available days into `allDays` (stable, write-once).
 *   Phase 2+ – every subsequent search marks `selectedDayValue` as selected in the session_day filter group.
 *
 * All URL updates use history.replaceState (no new browser history entries).
 */
import store from '@/search/store/index';

const baseExecuteSearch = store.actions.executeSearch;

const state = {
  ...store.state,
  // Stable list of all available days, sorted chronologically. Populated once
  // from Phase 1 (unfiltered search). Never changes after first population.
  allDays: [],
  // ISO date string of the currently selected day (e.g. "2026-06-05"), or null.
  selectedDayValue: null,
  search: {
    ...store.state.search,
    sortOption: { fieldName: 'startTime', sortMode: 'asc', displayName: 'time' },
  },
};

const getters = {
  ...store.getters,
  allDays: state => state.allDays,
  selectedDayValue: state => state.selectedDayValue,
};

const mutations = {
  ...store.mutations,

  /**
   * Parse q, filters, sort, day, and page from URL query parameters.
   */
  parseInitialQueryParams(state) {
    const url = new URL(window.location.href);
    const params = new URLSearchParams(url.search);

    // q (search term)
    const queryKey = state.options.queryStringKeyQuery || 'query';
    const query = params.get(queryKey);
    if (query) state.search.query = query;

    // filters (comma-separated filter IDs)
    const filterKey = state.options.queryStringKeyFilters || 'filters';
    const filters = params.get(filterKey);
    if (filters) {
      state.preselectedFilters = filters.split(',');
    }

    // page
    const pageKey = state.options.queryStringKeyPage || 'page';
    const page = params.get(pageKey);
    if (page && !isNaN(page) && !state.options.preserveResults) {
      let pageNum = Number(page);
      if (pageNum > 0) pageNum--;
      state.search.page = pageNum;
    }

    // sort — schedule-specific extension
    const sort = params.get('sort');
    if (sort) {
      const sortMap = {
        'title_normalized_asc':          { fieldName: 'title_normalized',          sortMode: 'asc',  displayName: 'a-z' },
        'title_normalized_desc':         { fieldName: 'title_normalized',          sortMode: 'desc', displayName: 'z-a' },
        'session_format_normalized_asc': { fieldName: 'session_format_normalized', sortMode: 'asc',  displayName: 'session type' },
        'startTime_asc':                 { fieldName: 'startTime',                 sortMode: 'asc',  displayName: 'time' },
      };
      if (sortMap[sort]) {
        state.search.sortOption = sortMap[sort];
      }
      // Unrecognised values (e.g. old ?sort=startTime bookmarks) fall back to the default.
    }

    // day — stored directly in selectedDayValue; Phase 1 still runs first
    // (unfiltered) to populate allDays, then Phase 2 uses this value.
    const day = params.get('day');
    if (day) {
      state.selectedDayValue = day;
    }

    // swapcardEventId — permanent extended filter scoping all searches
    if (state.options.swapcardEventId) {
      state.search.extended = {
        ...(state.search.extended || {}),
        swapcardEventIdFilter: state.options.swapcardEventId,
        contentTypeFilter: 'swapcard_session',
      };
    }
  },

  /**
   * Write q, filters, sort, day, and page to URL query parameters.
   * Uses history.replaceState (no new browser history entries).
   */
  updateQueryParams(state) {
    const url = new URL(window.location.href);
    const params = new URLSearchParams();

    // q (search term)
    const queryKey = state.options.queryStringKeyQuery || 'query';
    const query = (state.search.query || '').trim();
    if (query) params.set(queryKey, query);

    // filters (selected filter IDs, excluding session_day)
    const filterKey = state.options.queryStringKeyFilters || 'filters';
    const filterIds = collectSelectedFilterIds(state.search.filters, ['session_day']);
    if (filterIds.length) params.set(filterKey, filterIds.join(','));

    // sort (omit default startTime — it is the new default)
    const sort = state.search.sortOption;
    const isDefaultSort = !sort || sort.fieldName === 'startTime';
    if (!isDefaultSort && sort.fieldName) {
      params.set('sort', `${sort.fieldName}_${sort.sortMode}`);
    }

    // day — read directly from selectedDayValue (not from session_day facets)
    if (state.selectedDayValue) params.set('day', state.selectedDayValue);

    // page (omit first page)
    const pageKey = state.options.queryStringKeyPage || 'page';
    if (!state.options.preserveResults && state.search.page > 0) {
      params.set(pageKey, state.search.page + 1);
    }

    const paramsExist = !params.keys().next().done;
    const newURL = `${url.pathname}${paramsExist ? `?${params.toString()}` : ''}${url.hash}`;
    const historyState = state.options.paginationUsesHistory
      ? { page: state.search.page }
      : {};
    history.replaceState(historyState, '', decodeURIComponent(newURL));
  },

  /**
   * Capture all available days. Idempotent — ignored once populated.
   */
  setAllDays(state, days) {
    if (state.allDays.length === 0) {
      state.allDays = days;
    }
  },

  /**
   * Set the currently selected day.
   */
  setSelectedDay(state, value) {
    state.selectedDayValue = value;
  },
};

const actions = {
  ...store.actions,

  /**
   * Two-phase search execution.
   *
   * Phase 1 (allDays empty): Run without a day filter so that the session_day
   * facets in the response include all available days. After the response,
   * freeze allDays and re-dispatch (Phase 2).
   *
   * Phase 2+ (allDays populated): Mark selectedDayValue as selected in data.filters
   * for the session_day group and call baseExecuteSearch directly.
   * If selectedDayValue is null, no day filter is applied (All Days default).
   */
  async executeSearch(context) {
    const { state, commit, dispatch } = context;

    // Phase 2+: stable day list exists — mark the selected day in the filter payload
    if (state.allDays.length > 0) {
      const dayValue = state.selectedDayValue;
      const phase2Callback = (data) => {
        data.isScheduleSearch = true;
        // Always normalize session_day selected state so that switching to All Days
        // (dayValue === null) clears any previously-selected day still in state.search.filters.
        // f.value === null is always false, so all entries become unselected when All Days.
        data.filters = (data.filters || []).map(group => {
          if (group.id !== 'session_day') return group;
          return {
            ...group,
            filters: (group.filters || []).map(f => ({
              ...f,
              selected: f.value === dayValue,
            })),
          };
        });
        return data;
      };
      return baseExecuteSearch(context, phase2Callback);
    }

    // Phase 1: no stable day list yet — run unfiltered to capture all days
    const phase1Callback = (data) => {
      data.isScheduleSearch = true;
      return data;
    };
    await baseExecuteSearch(context, phase1Callback);

    // Extract and sort day facets from the Phase 1 response
    const dayGroup = (state.search.filters || []).find(g => g.id === 'session_day');
    const days = [...(dayGroup ? dayGroup.filters : [])]
      .sort((a, b) => (a.value || '').localeCompare(b.value || ''));

    commit('setAllDays', days);

    // No days returned — nothing to filter by; skip Phase 2 to avoid an
    // infinite loop (allDays would stay empty and re-trigger Phase 1 forever).
    if (!days.length) return;

    // Phase 2: re-run with the selected day filter applied
    // Since allDays.length > 0 now, this goes to the Phase 2 branch above
    return dispatch('executeSearch');
  },

  /**
   * Fetch a full (un-paged) list of sessions for printing.
   *
   * Implementation note: rather than dispatch through `executeSearch` — which
   * commits `setResults` and would clobber the user's current listing state —
   * we issue a direct one-shot POST to the same endpoint with the same payload
   * shape, overridden for `pageSize` (and, for `scope === 'all'`, also
   * `searchTerm` / `filters` / `extended` to drop user-applied narrowing).
   *
   * Returns the `itemResults` array (the same shape `executeSearch` populates
   * into `state.results.itemResults`).
   *
   * @param {Object} _ctx
   * @param {{ scope: 'all' | 'current' }} payload
   * @returns {Promise<Array>}
   */
  async fetchAllForPrint({ state }, { scope } = {}) {
    const PRINT_PAGE_SIZE = 500;

    // Base payload mirrors `executeSearch`, with paging removed.
    let data = {
      searchIndex: state.options.searchIndex,
      searchTerm: state.search.query,
      filters: state.search.filters,
      currentPage: 0,
      pageSize: PRINT_PAGE_SIZE,
      preselectedFilters: state.preselectedFilters,
      extended: state.search.extended,
      isLrlPage: state.options.isLrlPage,
      includeHighlights: false,
      ...(state.search.sortOption && { sortOption: state.search.sortOption }),
      isScheduleSearch: true,
    };

    if (scope === 'all') {
      // Drop user-applied narrowing but preserve the schedule's permanent
      // event scoping (swapcardEventIdFilter / contentTypeFilter from extended).
      const extended = state.search.extended || {};
      const preservedExtended = {};
      if (extended.swapcardEventIdFilter) preservedExtended.swapcardEventIdFilter = extended.swapcardEventIdFilter;
      if (extended.contentTypeFilter) preservedExtended.contentTypeFilter = extended.contentTypeFilter;

      data = {
        ...data,
        searchTerm: '',
        filters: [],
        preselectedFilters: [],
        extended: preservedExtended,
      };
      // Also drop the day-selection filter logic — not applied since filters is [].
    } else {
      // scope === 'current': mirror Phase 2's session_day selection so the
      // current day is honoured even though our payload uses unmutated state filters.
      const dayValue = state.selectedDayValue;
      if (dayValue && state.allDays.length > 0) {
        data.filters = (data.filters || []).map(group => {
          if (group.id !== 'session_day') return group;
          return {
            ...group,
            filters: (group.filters || []).map(f => ({
              ...f,
              selected: f.value === dayValue,
            })),
          };
        });
      }
    }

    const response = await fetch(state.options.endpoint, {
      method: 'POST',
      mode: 'cors',
      cache: 'no-cache',
      credentials: 'same-origin',
      headers: { 'Content-Type': 'application/json' },
      redirect: 'follow',
      referrerPolicy: 'no-referrer',
      body: JSON.stringify(data),
    });
    if (!response.ok) return [];
    const results = await response.json();
    return (results && results.itemResults) || [];
  },
};

export default {
  state,
  getters,
  mutations,
  actions,
};

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

/**
 * Recursively collect selected filter IDs from all filter groups,
 * excluding groups whose id is in `excludeGroupIds`.
 */
function collectSelectedFilterIds(filters, excludeGroupIds = []) {
  const ids = [];
  const recurse = (filterList) => {
    filterList.forEach(f => {
      if (f.selected) ids.push(f.id);
      if (f.filters && f.filters.length) recurse(f.filters);
    });
  };
  (filters || []).forEach(group => {
    if (excludeGroupIds.includes(group.id)) return;
    recurse(group.filters || []);
  });
  return ids;
}
